import React, { useState, useEffect } from "react";
import axios from "axios";
import { Zap, RefreshCw, CheckCircle, XCircle, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RULE_DESCRIPTIONS = {
  no_contact_3:  { color: "bg-blue-50 border-blue-200",   dot: "bg-blue-400",   field: "threshold_days",  fieldLabel: "Days without contact", unit: "days" },
  no_contact_7:  { color: "bg-yellow-50 border-yellow-200", dot: "bg-yellow-400", field: "threshold_days",  fieldLabel: "Days without contact", unit: "days" },
  no_contact_14: { color: "bg-orange-50 border-orange-200", dot: "bg-orange-400", field: "threshold_days",  fieldLabel: "Days without contact", unit: "days" },
  post_quote:    { color: "bg-green-50 border-green-200",  dot: "bg-green-400",  field: "followup_days",   fieldLabel: "Follow-up in",         unit: "days after quote" },
};

export default function AutomationPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const fetchRules = async () => {
    try {
      const { data } = await axios.get(`${API}/automation/rules`, { withCredentials: true });
      setRules(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRules(); }, []);

  const toggleRule = async (rule) => {
    try {
      const { data } = await axios.put(`${API}/automation/rules/${rule.id}`, { enabled: !rule.enabled }, { withCredentials: true });
      setRules((prev) => prev.map((r) => r.id === rule.id ? data : r));
      toast.success(`Rule "${rule.name}" ${!rule.enabled ? "enabled" : "disabled"}`);
    } catch { toast.error("Failed to update rule"); }
  };

  const saveThreshold = async (rule) => {
    const val = parseInt(editValue);
    if (!val || val < 1) { toast.error("Value must be at least 1"); return; }
    const meta = RULE_DESCRIPTIONS[rule.rule_type];
    const updateBody = { [meta.field]: val };
    try {
      const { data } = await axios.put(`${API}/automation/rules/${rule.id}`, updateBody, { withCredentials: true });
      setRules((prev) => prev.map((r) => r.id === rule.id ? data : r));
      setEditingId(null);
      toast.success("Rule updated");
    } catch { toast.error("Failed to update"); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/automation/run`, {}, { withCredentials: true });
      setLastResult(data);
      if (data.tasks_created > 0) {
        toast.success(`Automation complete — ${data.tasks_created} task(s) created`);
      } else {
        toast.success("Automation ran — no new tasks needed");
      }
      fetchRules();
    } catch { toast.error("Automation run failed"); }
    finally { setRunning(false); }
  };

  const getFieldValue = (rule) => {
    const meta = RULE_DESCRIPTIONS[rule.rule_type];
    return rule[meta?.field] ?? "—";
  };

  if (loading) return <div className="p-8 text-gray-500">Loading...</div>;

  const lastRunDate = rules[0]?.last_run ? new Date(rules[0].last_run).toLocaleString() : "Never";
  const totalCreated = rules.reduce((s, r) => s + (r.total_created || 0), 0);

  return (
    <div data-testid="automation-page" className="p-8 max-w-2xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" /> Automation Rules
          </h1>
          <p className="text-sm text-gray-500 mt-1">Rules run automatically every time you open the dashboard.</p>
        </div>
        <button
          data-testid="run-automation-btn"
          onClick={runNow}
          disabled={running}
          className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-4 py-2 rounded-sm text-sm disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${running ? "animate-spin" : ""}`} />
          {running ? "Running..." : "Run Now"}
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-gray-200 rounded-sm p-3 text-center">
          <p className="text-2xl font-bold text-gray-900">{rules.filter(r => r.enabled).length}</p>
          <p className="text-xs text-gray-500 mt-0.5">Active rules</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-sm p-3 text-center">
          <p className="text-2xl font-bold text-gray-900">{totalCreated}</p>
          <p className="text-xs text-gray-500 mt-0.5">Tasks auto-created</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-sm p-3 text-center">
          <p className="text-sm font-semibold text-gray-700 truncate">{lastRunDate}</p>
          <p className="text-xs text-gray-500 mt-0.5">Last run</p>
        </div>
      </div>

      {lastResult && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-sm px-4 py-3 flex items-center gap-2 text-sm text-green-800">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          Last run: <strong>{lastResult.tasks_created} task(s) created</strong>, {lastResult.rules_run} rules checked
          <button onClick={() => setLastResult(null)} className="ml-auto text-green-400 hover:text-green-600"><XCircle className="w-4 h-4" /></button>
        </div>
      )}

      {/* Rules list */}
      <div className="space-y-3">
        {rules.map((rule) => {
          const meta = RULE_DESCRIPTIONS[rule.rule_type] || {};
          const isEditing = editingId === rule.id;
          return (
            <div key={rule.id} data-testid={`rule-card-${rule.rule_type}`}
              className={`border rounded-sm p-4 transition-opacity ${meta.color || "bg-gray-50 border-gray-200"} ${!rule.enabled ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5 ${meta.dot || "bg-gray-400"}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-900">{rule.name}</span>
                      {rule.source === "automation" && (
                        <span className="text-[9px] font-bold bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded uppercase">Auto</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>

                    {/* Editable threshold */}
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-gray-600">{meta.fieldLabel}:</span>
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number" min="1" max="90"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveThreshold(rule); if (e.key === "Escape") setEditingId(null); }}
                            className="w-14 text-xs px-2 py-1 border border-gray-300 rounded-sm bg-white"
                            autoFocus
                          />
                          <span className="text-xs text-gray-500">{meta.unit}</span>
                          <button onClick={() => saveThreshold(rule)} className="text-xs font-bold text-green-600 hover:text-green-800 px-2 py-0.5 bg-white border border-green-300 rounded-sm">Save</button>
                          <button onClick={() => setEditingId(null)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                        </div>
                      ) : (
                        <button
                          data-testid={`rule-threshold-${rule.rule_type}`}
                          onClick={() => { setEditingId(rule.id); setEditValue(String(getFieldValue(rule))); }}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded-sm hover:border-gray-400"
                        >
                          {getFieldValue(rule)} {meta.unit} <ChevronRight className="w-3 h-3 opacity-40" />
                        </button>
                      )}
                    </div>
                    {rule.total_created > 0 && (
                      <p className="text-[10px] text-gray-400 mt-1">{rule.total_created} tasks auto-created total</p>
                    )}
                  </div>
                </div>

                {/* Toggle */}
                <button
                  data-testid={`rule-toggle-${rule.rule_type}`}
                  onClick={() => toggleRule(rule)}
                  className={`relative flex-shrink-0 w-10 h-5 rounded-full transition-colors ${rule.enabled ? "bg-yellow-400" : "bg-gray-300"}`}
                  title={rule.enabled ? "Click to disable" : "Click to enable"}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${rule.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-400 mt-6">
        Automation runs silently each time you open the dashboard. Tasks marked "Auto" are generated by these rules.
        No duplicate tasks are created if a pending follow-up already exists for the client.
      </p>
    </div>
  );
}
